import { type ServiceParams, unwrapJson } from "../client";
import type {
  EvidenceFilters,
  EvidenceListResponse,
  EvidenceReferenceResponse,
  FrameworkCode,
  Iso42001MaturityResponse,
  ManualEvidenceRequest,
  MultiFrameworkReadinessResponse,
  NistMaturityResponse,
  SubscriptionCreateBody,
  SubscriptionListResponse,
  SubscriptionResponse,
} from "./types";

/**
 * Compliance service — wraps ``/v1/governance/compliance/*`` tenant
 * endpoints. Readiness + maturity + evidence reads are open to any
 * authenticated tenant user. Subscription POST/DELETE and evidence
 * POST/DELETE require tenant admin; the backend gates that — the
 * FE surfaces the controls and lets the 403 surface naturally for
 * non-admins.
 *
 * Spec note: the FE-3B spec listed ``getFrameworkPosture`` +
 * ``getGaps`` as separate methods, but the backend only ships
 * ``/readiness`` (with optional ``framework_code`` filter) that
 * already returns per-framework readiness and the gap_clauses
 * list. ``getReadiness`` covers both call-sites.
 */
const Compliance = ({ url, config }: ServiceParams) => {
  const base = `${url}/v1/governance/compliance`;

  const buildQuery = (filters?: Record<string, unknown>) => {
    const params = new URLSearchParams();
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  };

  return {
    async listSubscriptions(
      params: { active_only?: boolean } = {}
    ): Promise<SubscriptionListResponse> {
      const response = await fetch(
        `${base}/subscriptions${buildQuery(
          params as Record<string, unknown>
        )}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<SubscriptionListResponse>(response);
    },

    async subscribe(
      body: SubscriptionCreateBody
    ): Promise<SubscriptionResponse> {
      const response = await fetch(`${base}/subscriptions`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(body),
      });
      return unwrapJson<SubscriptionResponse>(response);
    },

    async unsubscribe(frameworkCode: FrameworkCode): Promise<void> {
      const response = await fetch(
        `${base}/subscriptions/${frameworkCode}`,
        { method: "DELETE", headers: config() }
      );
      if (!response.ok) return unwrapJson<void>(response);
    },

    /** Returns a list of supported framework codes + current
     *  version metadata. Surfaces the catalogue for the subscribe
     *  flow. */
    async listFrameworks(): Promise<Array<Record<string, unknown>>> {
      const response = await fetch(`${base}/frameworks`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<Array<Record<string, unknown>>>(response);
    },

    async getReadiness(
      params: { framework_code?: FrameworkCode } = {}
    ): Promise<MultiFrameworkReadinessResponse> {
      const response = await fetch(
        `${base}/readiness${buildQuery(params as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<MultiFrameworkReadinessResponse>(response);
    },

    async getNistMaturity(): Promise<NistMaturityResponse> {
      const response = await fetch(`${base}/maturity/nist`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<NistMaturityResponse>(response);
    },

    async getIso42001Maturity(): Promise<Iso42001MaturityResponse> {
      const response = await fetch(`${base}/maturity/iso42001`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<Iso42001MaturityResponse>(response);
    },

    async listEvidence(
      filters: EvidenceFilters = {}
    ): Promise<EvidenceListResponse> {
      const response = await fetch(
        `${base}/evidence${buildQuery(filters as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<EvidenceListResponse>(response);
    },

    async submitEvidence(
      body: ManualEvidenceRequest
    ): Promise<EvidenceReferenceResponse> {
      const response = await fetch(`${base}/evidence`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(body),
      });
      return unwrapJson<EvidenceReferenceResponse>(response);
    },

    async revokeEvidence(
      evidenceId: string,
      reason?: string
    ): Promise<void> {
      const qs = reason
        ? `?reason=${encodeURIComponent(reason)}`
        : "";
      const response = await fetch(`${base}/evidence/${evidenceId}${qs}`, {
        method: "DELETE",
        headers: config(),
      });
      if (!response.ok) return unwrapJson<void>(response);
    },
  };
};

export default Compliance;
